import { Panel } from "@xyflow/react";
import { useAtomValue } from "jotai";
import { sideBarWidthAtom } from "@zapier/canvas-state";
import { useEffect, useState, useRef, useLayoutEffect } from "react";

import styles from "./DiagrammingToolbar.module.css";
import { CursorOptions } from "./CursorOptions";
import { BasicNodeOptions } from "./BasicNodeOptions";
import { AppNodeOptions } from "./AppNodeOptions";

export function DiagrammingToolbar() {
  const [showDiagrammingToolbar, setShowDiagrammingToolbar] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setShowDiagrammingToolbar(true);
    }, 1500);
  }, []);

  // Calculate horizontal offset so the toolbar remains visually centred relative to the canvas pane
  const sideBarWidth = useAtomValue(sideBarWidthAtom);
  const rawOffset = Math.max(0, sideBarWidth / 2);

  /* ------------------------------------------------------------------
   * Clamp offset so the toolbar is never pushed off the right edge
   * ------------------------------------------------------------------ */
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [toolbarWidth, setToolbarWidth] = useState(0);

  // Measure on mount & on resize
  useLayoutEffect(() => {
    const updateSizes = () => {
      setViewportWidth(window.innerWidth);
      setToolbarWidth(toolbarRef.current?.offsetWidth ?? 0);
    };

    updateSizes();
    window.addEventListener("resize", updateSizes);
    return () => window.removeEventListener("resize", updateSizes);
  }, []);

  // Leave 16 px padding on the right side
  const maxSafeOffset = Math.max(0, viewportWidth / 2 - 16 - toolbarWidth / 2);
  const clampedOffset = Math.max(0, Math.min(rawOffset, maxSafeOffset));

  // Only apply the offset style after the component has mounted and we have real measurements
  const [isMounted, setIsMounted] = useState(false);

  /*
   * ⚠️ HYDRATION FIX: Prevents server/client mismatch with window-dependent calculations.
   * Server renders style={}, client renders style={{marginLeft: "242.5px"}} after mount.
   * DO NOT REMOVE - prevents "server rendered HTML didn't match client properties" error.
   */
  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <Panel
      position="bottom-center"
      className={`${styles.toolbar} ${
        showDiagrammingToolbar ? styles.toolbarVisible : ""
      }`}
      style={isMounted ? { marginLeft: `${clampedOffset}px` } : {}}
    >
      <div
        role="toolbar"
        aria-label="Shortcuts toolbar"
        className={styles.appListContainer}
        data-id="appsListContainer"
        ref={toolbarRef}
      >
        <div className={styles.section}>
          <div className={styles.sectionHeader}>Cursor</div>
          <CursorOptions isDisabled={false} />
        </div>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>Basic</div>
          <BasicNodeOptions isDisabled={false} />
        </div>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>Zapier Assets</div>
          <AppNodeOptions isDisabled={false} />
        </div>
      </div>
    </Panel>
  );
}
